import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useClient } from 'urql';

import {
  type GetPayV2Query,
  PAY_V2_QUERY,
  type PayV2Entity,
  type PayV2Req,
} from '@revelio/data-access';
import { NoData } from '@revelio/replots';

import type { SelectionListKey } from '../../../../../shared/selection-lists/types';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { useGeographyMode } from '../../../filters/hooks/useGeographyMode';
import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { getSecondaryPrimaryDimension } from '../../../filters/utils/get-secondary-primary-dimension';
import { getV2Filters } from '../../../filters/utils/getV2Filters';
import { entKindToPositionsEntityKind } from '../../../filters/utils/role-skill-ent-kind';
import { ChartFallback } from '../../Chart/ChartFallback';
import { useChart } from '../../Chart/ChartProvider';
import { isCompensationChartType } from '../../PageConfigProvider/types/chart';
import type { PayMetricValues } from '../../PageConfigProvider/types/metric';
import { usePageState } from '../../PageState/PageStateProvider';
import {
  type SupportedSecondaryDimensions,
  type TopEntity,
  TopNDataProvider,
} from '../TopNDataProvider';
import {
  type DataProviderChildren,
  DataProviderQueryStatus,
  type DataProviderSet,
} from '../types';
import { convertCompensationTopNData } from './convert-compensation-top-n-data';

type CompensationTopNDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: SupportedSecondaryDimensions;
  metric: PayMetricValues;
  exchangeRate?: number;
  children: DataProviderChildren<DataProviderSet | undefined>;
};

export const CompensationTopNDataProvider = ({
  primaryDimension,
  secondaryDimension,
  metric,
  exchangeRate,
  children,
}: CompensationTopNDataProviderProps) => {
  return (
    <TopNDataProvider
      primaryDimension={primaryDimension}
      secondaryDimension={secondaryDimension}
    >
      {({ data, status }) => (
        <ChartFallback status={status}>
          {data && (
            <CompensationMultiQuery
              primaryDimension={primaryDimension}
              secondaryDimension={secondaryDimension}
              topEntities={data}
              metric={metric}
              exchangeRate={exchangeRate}
            >
              {children}
            </CompensationMultiQuery>
          )}
          {!data && <NoData />}
        </ChartFallback>
      )}
    </TopNDataProvider>
  );
};

type PrimaryDimensionSelectionKeys = Extract<
  SelectionListKey,
  | 'rics_k10'
  | 'rics_k50'
  | 'rics_k400'
  | 'company'
  | 'region'
  | 'country'
  | 'msa'
  | 'role_k7'
  | 'role_k150'
  | 'role_k1500'
  | 'role_k15000'
>;
const CompensationMultiQuery = ({
  primaryDimension,
  secondaryDimension,
  metric,
  topEntities,
  exchangeRate,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: SupportedSecondaryDimensions;
  metric: PayMetricValues;
  topEntities: TopEntity[];
  exchangeRate?: number;
  children: DataProviderChildren<DataProviderSet | undefined>;
}) => {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { primaryFilter } = usePrimaryFilterState(primaryDimension);
  const geographyMode = useGeographyMode();

  const { item } = useChart();
  const { pageState } = usePageState();
  const selectedEntity = pageState.primaryEntities?.[item?.id];
  const chartType = item?.plot?.chartType;

  const selections = primaryFilter?.selections;
  const primarySelections =
    selections?.map(
      (
        selection
      ): Partial<Record<PrimaryDimensionSelectionKeys, number[]>> => ({
        [selection.levelId]: [
          Number(selection?.id?.split('_')?.slice(-1)?.[0]),
        ],
      })
    ) ?? [];

  const filteredPrimarySelections = selectedEntity
    ? primarySelections?.filter((selection) =>
        selection?.[
          selectedEntity.levelId as PrimaryDimensionSelectionKeys
        ]?.includes(Number(selectedEntity.id))
      )
    : primarySelections;

  const payV2Entities = topEntities
    .map((ent): PayV2Entity | null => {
      const EntType = entKindToPositionsEntityKind(ent.EntType);
      return EntType ? { EntID: ent.EntID, EntType } : null;
    })
    .filter((ent): ent is PayV2Entity => ent !== null);

  const compensationFilters = filteredPrimarySelections.map(
    (primarySelection): PayV2Req => ({
      entities: payV2Entities,
      filters: {
        ...getV2Filters({
          primaryDimension: getSecondaryPrimaryDimension(secondaryDimension),
          effectiveFilters,
        }),
        ...primarySelection,
      },
      ...(geographyMode === 'administrative' && {
        options: { geo_breakdown_state_over_msa: true },
      }),
    })
  );

  const gqlClient = useClient();

  const [fetching, setFetching] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [compensationData, setCompensationData] = useState<
    { dim: string; data: GetPayV2Query['pay_v2'] }[]
  >([]);

  const lastFilterRef = useRef<PayV2Req[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    (async () => {
      if (isEqual(lastFilterRef.current, compensationFilters)) {
        return;
      }

      setFetching(true);
      setHasError(false);

      try {
        const responses = await Promise.all(
          compensationFilters.map((filter) =>
            gqlClient.query(PAY_V2_QUERY, { req: filter })
          )
        );

        lastFilterRef.current = compensationFilters;

        setCompensationData(
          responses.map((response, i) => ({
            data: response.data?.pay_v2,
            dim: selections?.[i]?.name ?? '',
          }))
        );
        // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
      } catch (e) {
        setHasError(true);
      } finally {
        setFetching(false);
      }
    })();
  }, [compensationFilters, gqlClient]);

  const status = (() => {
    if (fetching) return DataProviderQueryStatus.Fetching;
    if (hasError) return DataProviderQueryStatus.Error;
    return DataProviderQueryStatus.Success;
  })();

  if (!chartType || !isCompensationChartType(chartType)) {
    return children({ data: undefined, status: DataProviderQueryStatus.Idle });
  }

  return children({
    data: convertCompensationTopNData({
      data: compensationData,
      metric,
      chartType: chartType ?? 'bar',
      groupBySegment: item?.plot?.groupBySegment,
      exchangeRate,
    }),
    status,
  });
};
